import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ThemeToggle } from "@/components/theme-toggle";
import { SiteFooter } from "@/components/site-footer";
import { IkomatchCTA } from "@/components/ikomatch-cta";
import { usePricing } from "@/lib/pricing";

const OG_IMAGE = "https://deckscore.ai/og-image.png";
const TITLE = "Score Your Pitch Deck Free in 2 Minutes - DeckScore";
const DESCRIPTION =
  "Score your pitch deck free in 2 minutes and see if it's investor-ready - then reach 10+ accelerators and VCs without a network. No card, no signup.";

const JSONLD_SOFTWARE = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "DeckScore",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description:
    "Free AI pitch deck scoring with a slide-by-slide investor-readiness score, plus one-form applications to 10+ matched accelerators and VCs.",
  url: "https://deckscore.ai",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    description: "Free pitch deck score and investor-readiness check",
  },
  publisher: { "@type": "Organization", name: "ikomatch", url: "https://ikomatch.com" },
};

const JSONLD_ORG = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "DeckScore",
  url: "https://deckscore.ai",
  logo: "https://deckscore.ai/logo.png",
  legalName: "Adviselo Collab Studios Private Limited",
  email: "info@ikomatch.com",
  telephone: "+91-9723461686",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Surat",
    addressRegion: "Gujarat",
    addressCountry: "IN",
  },
  parentOrganization: { "@type": "Organization", name: "ikomatch", url: "https://ikomatch.com" },
  founder: {
    "@type": "Person",
    name: "Mehul Shah",
    jobTitle: "Founder",
    sameAs: ["https://www.linkedin.com/in/mehul87/", "https://x.com/mehulshahca"],
  },
  sameAs: ["https://x.com/mehulshahca", "https://ikomatch.com"],
};

const JSONLD_FAQ = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Is the paid submission actually worth it, or am I just paying to get ignored?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The deck score is free - you risk nothing to find out if you're ready. The one-time fee covers a human transaction advisor entering your full deck + application details into the systems of 10+ matched accelerators, incubators and VCs (plus fitting grant programmes) - not a scraped list - and if we don't submit within 48 hours, you get it back. Run the free score first; decide on the paid submission after.",
      },
    },
    {
      "@type": "Question",
      name: "Are you legit, or is this a weekend project that's gone in a month?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The outreach engine behind this, ikomatch, is already live: our first cohort of 15 founders averaged 500+ investors reached and 10+ meetings in 45 days. Poke around ikomatch.com, or just run the free score - no card, nothing to cancel.",
      },
    },
    {
      "@type": "Question",
      name: "Is this actually for me, or for founders who already have it figured out?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "It's built for first-time founders with a finished deck and a blank contact list, raising a first round with no network. The score is free and takes two minutes, so you'll know if it fits before you commit anything.",
      },
    },
  ],
};

export const Route = createFileRoute("/")({
  component: Landing,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://deckscore.ai/" },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "DeckScore - a scored pitch-deck tile flowing into investor inboxes. Headline: Your deck's ready. Now who gets it?" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
      { name: "twitter:image:alt", content: "DeckScore - a scored pitch-deck tile flowing into investor inboxes. Headline: Your deck's ready. Now who gets it?" },
    ],
    links: [{ rel: "canonical", href: "https://deckscore.ai/" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(JSONLD_SOFTWARE) },
      { type: "application/ld+json", children: JSON.stringify(JSONLD_ORG) },
      { type: "application/ld+json", children: JSON.stringify(JSONLD_FAQ) },
    ],
  }),
});

function EmailForm({ id, centered = false }: { id: string; centered?: boolean }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      sessionStorage.setItem("deckready_email", email);
    } catch {}
    try {
      await supabase.from("leads").insert({ email, source: "landing" });
    } catch {
      // best-effort; never block the user
    }
    setLoading(false);
    navigate({ to: "/upload" });
  }

  return (
    <form
      onSubmit={onSubmit}
      className={`flex w-full max-w-[460px] flex-col gap-2.5 sm:flex-row ${centered ? "mx-auto" : ""}`}
      id={id}
    >
      <input
        type="email"
        required
        placeholder="you@startup.com"
        aria-label="Your email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="min-h-[52px] flex-1 rounded-xl border border-line bg-surface px-4 text-base text-ink placeholder:text-[#6c7c8c] focus:border-accent focus:outline-none"
      />
      <button
        type="submit"
        disabled={loading}
        className="inline-flex min-h-[52px] items-center justify-center gap-2 whitespace-nowrap rounded-full border-none bg-accent px-6 text-[17px] font-bold text-accent-ink transition hover:-translate-y-px hover:bg-accent-press disabled:opacity-70"
      >
        {loading ? "Saving…" : "Analyze my deck free →"}
      </button>
    </form>
  );
}

function CtaButton({ href = "#start", children = "Analyze my deck free →" }: { href?: string; children?: React.ReactNode }) {
  return (
    <a
      href={href}
      className="inline-flex min-h-[52px] items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent px-6 text-[17px] font-bold text-accent-ink transition hover:-translate-y-px hover:bg-accent-press"
    >
      {children}
    </a>
  );
}

function Landing() {
  const pricing = usePricing();
  const navigate = useNavigate();
  useEffect(() => {
    const hasAuthReturn =
      (typeof window !== "undefined") &&
      (window.location.hash.includes("access_token") ||
       new URLSearchParams(window.location.search).has("code"));
    if (!hasAuthReturn) return;
    let done = false;
    const go = () => { if (!done) { done = true; navigate({ to: "/dashboard" }); } };
    supabase.auth.getSession().then(({ data }) => { if (data.session) go(); });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => { if (session) go(); });
    return () => sub.subscription.unsubscribe();
  }, [navigate]);
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="overflow-x-hidden">
      {/* HEADER */}
      <header className="sticky top-0 z-50 border-b border-line bg-bg/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-[1120px] items-center justify-between gap-3 px-5">
          <div className="flex min-w-0 items-center gap-2.5 text-lg font-semibold tracking-tight">
            <span className="inline-block h-[22px] w-[22px] shrink-0 rounded-md bg-accent" />
            <span className="truncate">DeckScore</span>
          </div>
          {/* Desktop nav */}
          <div className="hidden items-center gap-5 md:flex">
            <a href="#how" className="text-[15px] font-medium text-muted-ink hover:text-ink">How it works</a>
            <a href="#proof" className="text-[15px] font-medium text-muted-ink hover:text-ink">Founders</a>
            <a href="#faq" className="text-[15px] font-medium text-muted-ink hover:text-ink">FAQ</a>
            <Link to="/grants" className="text-[15px] font-medium text-muted-ink hover:text-ink">Grants</Link>
            <Link to="/apply" className="text-[15px] font-medium text-muted-ink hover:text-ink">Reach Investors</Link>
            <Link to="/for-investors" className="text-[15px] font-medium text-muted-ink hover:text-ink">For Investors</Link>
            <ThemeToggle />
            <Link to="/login" className="text-[15px] font-medium text-muted-ink hover:text-ink">
              Log in
            </Link>
            <a
              href="#start"
              className="inline-flex min-h-11 items-center justify-center rounded-full bg-accent px-4 text-[15px] font-bold text-accent-ink transition hover:bg-accent-press"
            >
              Analyze my deck free →
            </a>
          </div>
          {/* Mobile: CTA + hamburger */}
          <div className="flex items-center gap-2 md:hidden">
            <a
              href="#start"
              className="inline-flex h-11 items-center justify-center rounded-full bg-accent px-3.5 text-[13.5px] font-bold text-accent-ink transition hover:bg-accent-press"
            >
              Analyze free →
            </a>
            <button
              type="button"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              aria-controls="mobile-menu-panel"
              onClick={() => setMenuOpen((v) => !v)}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-line bg-surface text-ink"
            >
              <span className="sr-only">Menu</span>
              {menuOpen ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M4 7h16M4 12h16M4 17h16"/></svg>
              )}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div
            id="mobile-menu-panel"
            className="border-t border-line bg-bg/95 backdrop-blur-md md:hidden"
          >
            <nav className="mx-auto flex max-w-[1120px] flex-col px-5 py-2">
              {[
                { href: "#how", label: "How it works" },
                { href: "#proof", label: "Founders" },
                { href: "#faq", label: "FAQ" },
              ].map((it) => (
                <a
                  key={it.href}
                  href={it.href}
                  onClick={() => setMenuOpen(false)}
                  className="flex min-h-11 items-center text-[16px] font-medium text-ink"
                >
                  {it.label}
                </a>
              ))}
              <Link to="/grants" onClick={() => setMenuOpen(false)} className="flex min-h-11 items-center text-[16px] font-medium text-ink">Grants</Link>
              <Link to="/apply" onClick={() => setMenuOpen(false)} className="flex min-h-11 items-center text-[16px] font-medium text-ink">Reach Investors</Link>
              <Link to="/for-investors" onClick={() => setMenuOpen(false)} className="flex min-h-11 items-center text-[16px] font-medium text-ink">For Investors</Link>
              <Link to="/login" onClick={() => setMenuOpen(false)} className="flex min-h-11 items-center text-[16px] font-medium text-ink">Log in</Link>
              <div className="flex min-h-11 items-center justify-between">
                <span className="text-[16px] font-medium text-ink">Theme</span>
                <ThemeToggle />
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* HERO */}
      <section id="start" className="bg-peach px-5 pb-10 pt-10 sm:pt-16">
        <div className="mx-auto grid max-w-[1120px] items-center gap-9 md:grid-cols-[1.05fr_0.95fr] md:gap-12">
          <div>
            <span className="mb-5 inline-block font-script text-accent text-[24px] leading-none">
              For first-time founders raising their first round
            </span>
            <h1 className="mb-4 text-[clamp(34px,5.4vw,60px)] font-semibold leading-[1.03] tracking-tight">
              Your deck's ready.<br />Now who gets it?
            </h1>
            <p className="mb-6 max-w-[520px] text-[17px] text-muted-ink sm:text-[19px]">
              Score your deck free in 2 minutes and see if it's investor-ready - then reach real investors without a network.
            </p>
            <EmailForm id="hero-form" />
            <div className="mt-3 flex flex-wrap gap-x-3.5 gap-y-1 text-sm text-muted-ink">
              <span>✓ Free slide-by-slide score</span>
              <span>✓ No card</span>
              <span>✓ 2 minutes</span>
            </div>
            <p className="mt-3 text-[13.5px] text-muted-ink">
              Deck already strong?{" "}
              <IkomatchCTA placement="home_secondary" className="text-[13.5px] font-medium">
                Let ikomatch run personalized investor outreach for you →
              </IkomatchCTA>
            </p>
            <div className="mt-4 max-w-[460px] border-t border-line pt-4 text-[14.5px] text-muted-ink">
              First cohort · 15 founders averaged <b className="font-bold text-accent">500+ investors reached</b> and <b className="font-bold text-accent">10+ meetings</b> in 45 days.
            </div>
          </div>

          {/* Score card visual */}
          <div className="rounded-[20px] border border-line bg-surface p-5 shadow-[0_24px_60px_-18px_rgba(0,0,0,0.22)] ring-1 ring-black/5" aria-hidden="true">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2.5 text-sm text-muted-ink">
                <span className="h-2.5 w-2.5 rounded-full bg-accent" />
                seed-deck-v4.pdf
              </div>
              <div className="rounded-full bg-accent/10 px-2.5 py-1 text-xs font-bold text-accent">Investor-ready check</div>
            </div>
            <div className="mb-5 flex items-center gap-5">
              <div className="relative h-[118px] w-[118px] flex-none">
                <svg width="118" height="118" viewBox="0 0 118 118" className="-rotate-90">
                  <circle cx="59" cy="59" r="52" fill="none" stroke="#E3DFDA" strokeWidth="12" />
                  <circle
                    cx="59"
                    cy="59"
                    r="52"
                    fill="none"
                    stroke="#B79AEF"
                    strokeWidth="12"
                    strokeLinecap="round"
                    strokeDasharray="326.7"
                    strokeDashoffset="91.5"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-[32px] font-semibold tracking-tight leading-none">72</span>
                  <span className="text-[11px] uppercase tracking-wider text-muted-ink">/ 100</span>
                </div>
              </div>
              <div className="text-sm text-muted-ink">
                Close. <b className="font-semibold text-ink">3 fixes</b> stand between this deck and a confident send. Two slides are dragging your score down.
              </div>
            </div>
            <div className="flex flex-col gap-2.5">
              <ScoreRow color="g" label="Problem & market" note="Strong" />
              <ScoreRow color="a" label="Traction slide" note="Vague numbers" />
              <ScoreRow color="r" label="The ask" note="Missing - add it" flag />
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section id="problem" className="bg-sky px-5 py-16">
        <div className="mx-auto max-w-[1120px]">
          <div className="mb-3.5 text-[13px] font-bold uppercase tracking-widest text-accent">The part nobody warned you about</div>
          <p className="mb-8 max-w-[760px] text-[18px] leading-[1.6] sm:text-[20px]">
            You finished the deck. You rewrote the intro slide four times. And now it's done - sitting in a folder - and you're staring at the part nobody warned you about: you don't know who to send it to, or whether it's even good enough to send. No warm network. No VC on speed dial. Just a blank contact list and a quiet dread that the deck's the problem… or maybe it's you.
          </p>
          <div className="grid gap-4 md:grid-cols-3">
            {[
              "I've filled the same application form on ten different VC sites. Name, stage, ask, deck link - again. And again.",
              "I cold-emailed 30 investors off a scraped list. Two opened it. Zero replied. Was it the deck or the targeting? No idea.",
              "Everyone says 'get a warm intro.' Cool - from whom? I don't know one person who knows one VC.",
            ].map((q) => (
              <div key={q} className="rounded-xl border border-line border-l-[3px] border-l-red bg-surface p-5 text-base italic text-ink">
                "{q}"
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTION */}
      <section id="how" className="px-5 py-16">
        <div className="mx-auto max-w-[1120px]">
          <div className="mb-3.5 text-[13px] font-bold uppercase tracking-widest text-accent">How it works</div>
          <h2 className="mb-4 text-[clamp(28px,3.6vw,40px)] font-semibold leading-[1.1] tracking-tight">Score first. Then reach investors - two ways.</h2>
          <p className="max-w-[660px] text-[18px] text-muted-ink sm:text-[19px]">
            Upload your deck, get an honest read in two minutes, and walk out with the fixes and a real path to investors.
          </p>
          <div className="mt-9 grid gap-4 md:grid-cols-3">
            <Step n={1} title="Drop in your deck" desc="PDF or a link. No form to fill, no account to set up first.">
              <div className="rounded-xl border border-line bg-surface-sunken p-3.5">
                <div className="rounded-lg border-[1.5px] border-dashed border-[#35485a] p-4 text-center text-sm text-muted-ink">
                  ⬆ &nbsp;seed-deck-v4.pdf
                  <br />
                  <span className="text-xs text-muted-ink">drag &amp; drop</span>
                </div>
              </div>
            </Step>
            <Step n={2} title="Get an honest score" desc="We read every slide against what investors actually check, flag what's weak, and match you to investors that fund your stage.">
              <div className="rounded-xl border border-line bg-surface-sunken p-3.5 text-sm">
                <MiniRow color="#5FAE7C">Problem & market - strong</MiniRow>
                <MiniRow color="#C99A3E">Traction - tighten numbers</MiniRow>
                <MiniRow color="#C65C4E">The ask - missing</MiniRow>
              </div>
            </Step>
            <Step n={3} title="Reach investors" desc="Your score unlocks two ready-to-go paths - apply to 10+ programs in one form, or let an agent run the outreach.">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between rounded-lg border border-line bg-surface-sunken px-3 py-2.5 text-sm">
                  <span>One form → 10+ programs</span>
                  <span className="rounded-full bg-accent/25 px-2 py-0.5 text-[11px] font-bold text-[#16171A]">{pricing.submissionAmountDisplay}</span>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-line bg-surface-sunken px-3 py-2.5 text-sm">
                  <span>ikomatch outreach agent</span>
                  <span className="rounded-full bg-[rgba(56,189,248,0.20)] px-2 py-0.5 text-[11px] font-bold text-[#0e5a73]">Free</span>
                </div>
              </div>
            </Step>
          </div>
        </div>
      </section>

      <hr className="border-none bg-line h-px" />

      {/* SOCIAL PROOF */}
      <section id="proof" className="bg-peach px-5 py-16">
        <div className="mx-auto max-w-[1120px]">
          <div className="mb-3.5 text-center text-[13px] font-bold uppercase tracking-widest text-accent">Founders from</div>
          <div className="mx-auto mb-10 flex max-w-[820px] flex-wrap justify-center gap-2.5">
            {[
              "Y Combinator Startup School",
              "On Deck",
              "Network School",
              "Indie Hackers",
              "r/startups",
              "Lenny's Newsletter",
            ].map((c) => (
              <span key={c} className="rounded-full border border-line bg-surface px-3.5 py-2 text-sm text-muted-ink">{c}</span>
            ))}
          </div>

          <div className="mx-auto mb-9 max-w-[820px] rounded-[20px] border border-accent/25 bg-gradient-to-br from-accent/10 to-accent/0 p-7 text-center">
            <span className="mb-3.5 inline-block rounded-full bg-accent/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-accent">First cohort · 15 founders</span>
            <p className="mx-auto max-w-[640px] text-[clamp(22px,3vw,30px)] font-semibold leading-tight tracking-tight">
              20 minutes a day for 45 days → your deck reaches <b className="text-accent">500+ investors</b> and books <b className="text-accent">10+ meetings</b>, on average.
            </p>
            <p className="mt-3 text-sm text-muted-ink">Averaged across the 15 startups in our first cohort.</p>
          </div>

          <div className="mb-9 grid gap-4 md:grid-cols-2">
            <Testimonial
              quote={`"Honestly the score stung - 61/100, and it was right about my traction slide. I fixed the three things it flagged and ran the one-form apply. Four investor calls booked inside two weeks. I'd sent 30 cold emails before that and gotten two opens."`}
              name="Priya N."
              role="Solo founder · pre-seed SaaS"
            />
            <Testimonial
              quote={`"Took me a couple days to actually commit to the 20-minutes-a-day thing. Once I did, the agent kept surfacing investors I'd never have found on my own. By day 40 I'd had 11 investor responses and two term-sheet conversations."`}
              name="Daniel O."
              role="Co-founder · seed climate-tech"
            />
          </div>

          <div className="mx-auto max-w-[560px] rounded-2xl border border-line bg-surface p-7 text-center transition hover:border-accent">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-accent pl-1 text-xl text-accent-ink">▶</div>
            <div className="text-[15px] text-ink"><b className="font-bold">Priya N.</b>, pre-seed SaaS · booked 4 investor calls in 2 weeks</div>
            <div className="mt-2 text-[12.5px] text-muted-ink">60-sec founder walkthrough - placeholder, record within 2 weeks</div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="bg-sky px-5 py-16">
        <div className="mx-auto max-w-[1120px]">
          <div className="mb-3.5 text-[13px] font-bold uppercase tracking-widest text-accent">Why founders use it</div>
          <h2 className="mb-4 text-[clamp(28px,3.6vw,40px)] font-semibold leading-[1.1] tracking-tight">Three things, done right.</h2>
          <div className="mt-9 grid gap-4 md:grid-cols-3">
            <Feature
              icon={
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#B79AEF" strokeWidth="2" strokeLinecap="round">
                  <path d="M12 3a9 9 0 1 0 9 9" />
                  <path d="M12 12l5-3" />
                </svg>
              }
              title="Know if your deck is ready - before an investor decides for you"
              desc="A free slide-by-slide score against what investors actually check, with the exact fixes to make."
            />
            <Feature
              icon={
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#B79AEF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="4" width="7" height="7" rx="1" />
                  <path d="M14 6h6M14 10h6M14 15h6M14 19h6" />
                </svg>
              }
              title="Reach 10+ investors with one form - not ten"
              desc="Fill one Investor Submission. A human transaction advisor enters your full deck + application details into each of the 10+ matched accelerators, incubators & early-stage VCs (and fitting grant programmes) within 48 hours."
            />
            <Feature
              icon={
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#B79AEF" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
                </svg>
              }
              title="An agent that keeps finding investors while you build"
              desc="ikomatch reaches matched investors over email and LinkedIn, personalized to each. Free to start; results compound."
            />
          </div>
        </div>
      </section>

      {/* MID CTA BAND - Reach Investors */}
      <section className="px-5 py-16">
        <div className="mx-auto max-w-[1120px]">
          <div className="rounded-[20px] border border-accent/25 bg-gradient-to-br from-accent/10 to-accent/0 p-8 text-center sm:p-10">
            <h2 className="mb-2.5 text-[clamp(28px,3.6vw,40px)] font-semibold leading-[1.1] tracking-tight">Deck ready? Reach 10+ investors.</h2>
            <p className="mb-6 text-[17px] text-muted-ink sm:text-[18px]">One form. A transaction advisor submits your deck + application details into 10+ matched accelerators, incubators &amp; VCs within 48h - {pricing.submissionAmountDisplay}.</p>
            <Link
              to="/apply"
              className="inline-flex min-h-[52px] items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent px-6 text-[17px] font-bold text-accent-ink transition hover:-translate-y-px hover:bg-accent-press"
            >
              Reach 10+ investors - {pricing.submissionAmountDisplay} →
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="px-5 py-16">
        <div className="mx-auto max-w-[820px]">
          <div className="mb-3.5 text-[13px] font-bold uppercase tracking-widest text-accent">Straight answers</div>
          <h2 className="mb-4 text-[clamp(28px,3.6vw,40px)] font-semibold leading-[1.1] tracking-tight">Questions founders ask.</h2>
          <div className="mt-6">
            <FaqItem q={`Is the ${pricing.submissionAmountDisplay} actually worth it, or am I just paying to get ignored?`}>
              The deck score is free - you risk nothing to find out if you're ready. The {pricing.submissionAmountDisplay} covers a human transaction advisor entering your full deck + application details into the systems of 10+ matched accelerators, incubators and VCs (plus fitting grant programmes) - not a scraped list - and if we don't submit within 48 hours, you get it back. Run the free score first; decide on the {pricing.submissionAmountDisplay} after you've seen it.
            </FaqItem>
            <FaqItem q="Are you legit, or is this a weekend project that's gone in a month?">
              Fair - most tools like this don't last. The outreach engine behind this, ikomatch, is already live: our first cohort of 15 founders averaged 500+ investors reached and 10+ meetings in 45 days. Poke around ikomatch.com, or just run the free score - no card, nothing to cancel.
            </FaqItem>
            <FaqItem q="Is this actually for me, or for founders who already have it figured out?">
              If you've already got partners at funds replying to your texts, skip us - you don't need this. It's built for first-time founders with a finished deck and a blank contact list, raising a first round with no network. The score is free and takes two minutes, so you'll know if it fits before you commit anything.
            </FaqItem>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-peach px-5 py-16 text-center">
        <div className="mx-auto max-w-[1120px]">
          <h2 className="mb-4 text-[clamp(28px,3.6vw,40px)] font-semibold leading-[1.1] tracking-tight">See if your deck is investor-ready.</h2>
          <p className="mx-auto max-w-[660px] text-[18px] text-muted-ink sm:text-[19px]">Free, in 2 minutes. Then reach real investors - two ways.</p>
          <div className="mt-7 flex justify-center">
            <EmailForm id="final-form" centered />
          </div>
          <div className="mt-2 text-[15px] text-muted-ink">Join the founders scoring their decks this week.</div>
          <a href="#how" className="mt-5 inline-block border-b border-transparent text-[15px] font-semibold text-accent hover:border-accent">
            How the score works →
          </a>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function ScoreRow({ color, label, note, flag }: { color: "g" | "a" | "r"; label: string; note: string; flag?: boolean }) {
  const dot = color === "g" ? "bg-accent" : color === "a" ? "bg-amber" : "bg-red";
  return (
    <div className={`flex items-center gap-3 rounded-lg border ${flag ? "border-red/40" : "border-line"} bg-surface-sunken px-3.5 py-3 text-sm`}>
      <span className={`h-2.5 w-2.5 flex-none rounded-full ${dot}`} />
      <span className="flex-1">{label}</span>
      <span className="text-[13px] text-muted-ink">{note}</span>
    </div>
  );
}

function Step({ n, title, desc, children }: { n: number; title: string; desc: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col rounded-2xl border border-line bg-surface p-5">
      <div className="mb-4 flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10 text-base font-semibold text-accent">{n}</div>
      <h3 className="mb-2 text-[18px] font-bold">{title}</h3>
      <p className="mb-4 flex-1 text-base text-muted-ink">{desc}</p>
      {children}
    </div>
  );
}

function MiniRow({ color, children }: { color: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2.5 py-1.5 text-muted-ink">
      <span className="h-[7px] w-[7px] rounded-full" style={{ backgroundColor: color }} />
      {children}
    </div>
  );
}

function Testimonial({ quote, name, role }: { quote: string; name: string; role: string }) {
  return (
    <div className="rounded-2xl border border-line bg-surface p-6">
      <div className="mb-4 text-[17px] leading-[1.55]">{quote}</div>
      <div className="flex items-center gap-3">
        <span className="h-10 w-10 flex-none rounded-full bg-gradient-to-br from-[#1f6feb] to-accent" />
        <div>
          <b className="block text-[15px] font-bold">{name}</b>
          <span className="text-[13px] text-muted-ink">{role}</span>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
  return (
    <div className="rounded-2xl border border-line bg-surface p-6">
      <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10">{icon}</div>
      <h3 className="mb-2 text-[18px] font-bold tracking-tight">{title}</h3>
      <p className="text-base text-muted-ink">{desc}</p>
    </div>
  );
}

function FaqItem({ q, children }: { q: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-line">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex min-h-11 w-full items-center justify-between gap-4 bg-transparent py-5 text-left text-[18px] font-semibold text-ink"
      >
        <span>{q}</span>
        <span className={`flex-none text-2xl text-accent transition-transform ${open ? "rotate-45" : ""}`}>+</span>
      </button>
      <div
        className="overflow-hidden text-[16.5px] leading-[1.6] text-muted-ink transition-[max-height] duration-300"
        style={{ maxHeight: open ? 400 : 0 }}
      >
        <p className="pb-5">{children}</p>
      </div>
    </div>
  );
}
